import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Download, Loader2, RefreshCw, Unplug } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { deelKeys } from "@/lib/queryKeys";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  disconnectDeel,
  getDeelStatus,
  startDeelOAuth,
  triggerDeelImport,
  triggerDeelSync,
  type DeelConnectionStatus,
} from "@/services/deelService";

export function SettingsDeelIntegrationCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  // Surface OAuth callback outcomes carried via redirect query.
  useEffect(() => {
    const flag = searchParams.get("deel");
    if (!flag) return;
    if (flag === "connected") {
      toast.success(t("settings.integrations.deel.syncSuccess"));
    } else if (flag === "error") {
      toast.error(t("settings.integrations.deel.stateError"));
    } else if (flag === "reconnect") {
      toast.error(t("settings.integrations.deel.stateErrorHint"));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("deel");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: deelKeys.status(),
    queryFn: getDeelStatus,
  });

  const isConnected = status?.status === "active";

  const invalidateStatus = () =>
    queryClient.invalidateQueries({ queryKey: deelKeys.status() });

  const authorizeMutation = useMutation({
    mutationFn: startDeelOAuth,
    onSuccess: ({ url }) => window.location.assign(url),
    onError: () => toast.error(t("settings.integrations.deel.stateError")),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectDeel,
    onSuccess: async () => {
      toast.success(t("settings.integrations.deel.disconnectSuccess"));
      await invalidateStatus();
    },
    onError: () => toast.error(t("settings.integrations.deel.disconnectError")),
  });

  const syncMutation = useMutation({
    mutationFn: triggerDeelSync,
    onSuccess: async () => {
      toast.success(t("settings.integrations.deel.syncSuccess"));
      await invalidateStatus();
    },
    onError: () => toast.error(t("settings.integrations.deel.syncError")),
  });

  const importMutation = useMutation({
    mutationFn: triggerDeelImport,
    onSuccess: async () => {
      toast.success(
        t("settings.integrations.deel.importSuccess", "Importación iniciada — tus facturas de Deel aparecerán como borradores."),
      );
      await invalidateStatus();
    },
    onError: () =>
      toast.error(t("settings.integrations.deel.importError", "No pudimos importar tus facturas de Deel.")),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-foreground">
                {t("settings.integrations.deel.title")}
              </CardTitle>
              <CardDescription>
                {t(
                  "settings.integrations.deel.description",
                  "Conectá tu cuenta de Deel y generamos automáticamente tus CFE a partir de las facturas que Deel emite.",
                )}
              </CardDescription>
            </div>
            <StateBadge status={status?.status ?? "not_connected"} loading={statusLoading} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.status === "error" && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="space-y-1">
                <p className="font-medium">{t("settings.integrations.deel.stateError")}</p>
                <p>{t("settings.integrations.deel.stateErrorHint")}</p>
                {status.lastError && (
                  <p className="text-xs text-destructive/80">{status.lastError}</p>
                )}
              </div>
            </div>
          )}

          {isConnected && (status?.deelUserEmail || status?.deelOrgName) && (
            <p className="text-sm text-muted-foreground">
              {status?.deelUserEmail
                ? t("settings.integrations.deel.connectedAs", "Conectado como {{email}}", {
                    email: status.deelUserEmail,
                  })
                : t("settings.integrations.deel.connectedToOrg", "Conectado a {{org}}", {
                    org: status.deelOrgName,
                  })}
              {status?.deelUserEmail && status?.deelOrgName
                ? ` · ${status.deelOrgName}`
                : ""}
            </p>
          )}

          {isConnected && (
            <p className="text-sm text-muted-foreground">
              {status?.lastSyncedAt
                ? t("settings.integrations.deel.lastSync", {
                    when: formatDisplayDate(status.lastSyncedAt),
                  })
                : t("settings.integrations.deel.noLastSync")}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {status?.status !== "active" && (
              <Button
                type="button"
                onClick={() => authorizeMutation.mutate()}
                disabled={authorizeMutation.isPending}
              >
                {authorizeMutation.isPending ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : null}
                {status?.status === "error"
                  ? t("settings.integrations.deel.reconnectButton")
                  : t("settings.integrations.deel.connectButton")}
              </Button>
            )}

            {isConnected && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 size-4" />
                  )}
                  {syncMutation.isPending
                    ? t("settings.integrations.deel.syncing")
                    : t("settings.integrations.deel.syncButton")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => importMutation.mutate()}
                  disabled={importMutation.isPending}
                >
                  {importMutation.isPending ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 size-4" />
                  )}
                  {importMutation.isPending
                    ? t("settings.integrations.deel.importing", "Importando…")
                    : t("settings.integrations.deel.importButton", "Importar facturas de Deel")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDisconnectOpen(true)}
                  disabled={disconnectMutation.isPending}
                >
                  <Unplug className="mr-2 size-4" />
                  {t("settings.integrations.deel.disconnectButton")}
                </Button>
              </>
            )}
          </div>

          {isConnected && (
            <p className="text-xs text-muted-foreground">
              {t(
                "settings.integrations.deel.invoicesHelp",
                "Cuando Deel emite una factura, generamos tu CFE automáticamente. Las facturas anteriores a la conexión se importan como borradores para que las revises.",
              )}
            </p>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title={t("settings.integrations.deel.disconnectButton")}
        description={t("settings.integrations.deel.disconnectConfirm")}
        confirmText={t("settings.integrations.deel.disconnectButton")}
        variant="destructive"
        isLoading={disconnectMutation.isPending}
        onConfirm={async () => {
          await disconnectMutation.mutateAsync();
          setDisconnectOpen(false);
        }}
      />
    </div>
  );
}

function StateBadge({
  status,
  loading,
}: {
  status: DeelConnectionStatus;
  loading: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
      </div>
    );
  }
  if (status === "active") {
    return <StatusBadge variant="success">{t("settings.integrations.deel.stateConnected")}</StatusBadge>;
  }
  if (status === "error") {
    return <StatusBadge variant="danger">{t("settings.integrations.deel.stateError")}</StatusBadge>;
  }
  if (status === "disconnected") {
    return <StatusBadge variant="neutral">{t("settings.integrations.deel.stateDisconnected")}</StatusBadge>;
  }
  return <StatusBadge variant="neutral">{t("settings.integrations.deel.stateNotConnected")}</StatusBadge>;
}
